import React, { useState } from "react";
import { api, authFetch } from "../utils/api";
import { compressImage } from "../utils/imageCompress";
import { useApp } from "../context/AppContext";
import { tTax } from "../utils/taxonomyI18n";

type PowerPhoto = { id: string; url: string };
export type Power = {
  id: string; label: string; years_experience?: number; description?: string;
  photos: PowerPhoto[]; missions_count: number;
};

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const CalendarIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

// Fiche Power façon AlloVoisins : portfolio de réalisations + missions réellement effectuées
// (pas déclaratif) plutôt qu'un simple libellé. Réutilisée sur son propre profil (édition) et
// sur celui d'un tiers (réservation).
export default function PowerCard({ power, isOwner, onDeleted, onPhotosChange, onManageAvailability, onBook }: {
  power: Power; isOwner: boolean;
  onDeleted?: () => void;
  onPhotosChange?: (photos: PowerPhoto[]) => void;
  onManageAvailability?: () => void;
  onBook?: () => void;
}) {
  const { lang } = useApp();
  const [uploading, setUploading] = useState(false);

  const addPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || power.photos.length >= 12) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const res = await authFetch("/uploads/power", { method: "POST", body: fd });
      const data = await res.json();
      if (data?.url) {
        const added = await api.post(`/users/me/powers/${power.id}/photos`, { url: data.url });
        if (added?.photo) onPhotosChange?.([...power.photos, added.photo]);
      }
    } catch {
      alert("Erreur lors de l'envoi de la photo");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (photoId: string) => {
    api.delete(`/users/me/powers/${power.id}/photos/${photoId}`).then(() => {
      onPhotosChange?.(power.photos.filter(p => p.id !== photoId));
    }).catch(() => {});
  };

  return (
    <div style={{ border: "1px solid #F0F0F0", borderRadius: 16, padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", backgroundColor: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{tTax(lang, power.label)}</div>
          <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6, marginTop: 4 }}>
            {power.years_experience ? (
              <span style={{ fontSize: 11, color: "#666", backgroundColor: "#F5F5F5", borderRadius: 10, padding: "2px 8px" }}>{power.years_experience} ans d'expérience</span>
            ) : null}
            {power.missions_count > 0 && (
              <span style={{ fontSize: 11, color: "#0a0a0a", fontWeight: 700, backgroundColor: "#F0F0F0", borderRadius: 10, padding: "2px 8px" }}>
                {power.missions_count} mission{power.missions_count > 1 ? "s" : ""} réalisée{power.missions_count > 1 ? "s" : ""}
              </span>
            )}
          </div>
          {power.description && <div style={{ fontSize: 12.5, color: "#666", marginTop: 6, lineHeight: 1.5 }}>{power.description}</div>}
        </div>
        {isOwner && onDeleted && (
          <button onClick={onDeleted} style={{ border: "none", background: "none", cursor: "pointer", padding: 0, fontSize: 16, fontWeight: 700, color: "#999", lineHeight: 1, flexShrink: 0 }}>×</button>
        )}
      </div>

      {/* Portfolio — réalisations passées (avant/après, exemples de travaux) */}
      {(power.photos.length > 0 || isOwner) && (
        <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 8, overflowX: "auto", marginTop: 12, paddingTop: 2 }}>
          {power.photos.map(photo => (
            <div key={photo.id} style={{ position: "relative", flexShrink: 0 }}>
              <img src={photo.url} alt="" style={{ width: 72, height: 72, borderRadius: 10, objectFit: "cover", display: "block" }} />
              {isOwner && (
                <button onClick={() => removePhoto(photo.id)} style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", backgroundColor: "#0a0a0a", color: "white", border: "2px solid white", fontSize: 10, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
              )}
            </div>
          ))}
          {isOwner && power.photos.length < 12 && (
            <label style={{ width: 72, height: 72, borderRadius: 10, border: "1.5px dashed #E0E0E0", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#999" }}>
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={addPhoto} disabled={uploading} />
              {uploading ? <span style={{ fontSize: 10 }}>...</span> : <PlusIcon />}
            </label>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        {isOwner && onManageAvailability && (
          <button onClick={onManageAvailability} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 4px", borderRadius: 10, border: "1px solid #E0E0E0", backgroundColor: "#F5F5F5", cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#333" }}>
            <CalendarIcon /> Gérer mon agenda
          </button>
        )}
        {!isOwner && onBook && (
          <button onClick={onBook} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, padding: "9px 4px", borderRadius: 10, border: "none", backgroundColor: "#0a0a0a", color: "white", cursor: "pointer", fontSize: 12.5, fontWeight: 700 }}>
            <CalendarIcon /> Réserver un créneau
          </button>
        )}
      </div>
    </div>
  );
}
